/**
 * Admin block, web side: recognising the server's refusal, the support draft,
 * and two tiny signals: blocked (the socket or fetch layer asks the gate to
 * re-check) and unblocked (the gate asks the socket layer to re-dial).
 * Spec: docs/superpowers/specs/2026-09-17-block-user-design.md
 */
import { ACCOUNT_SUPPORT_EMAIL, type AccountBlockStatus } from "@bash/shared";

/** A 403 whose body carries code 'account_blocked'. Reads a clone: the caller keeps the body. */
export async function isAccountBlockedResponse(
  res: Response
): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.clone().json()) as { code?: unknown } | null;
    return body?.code === "account_blocked";
  } catch {
    return false;
  }
}

/**
 * A refusal the gate should act on: signals it and says whether it did. For the
 * surfaces that call fetch directly (the arrival ceremony has no socket and no
 * shared client, so nothing else would ever land the takeover there).
 */
export async function noteAccountBlockedResponse(
  res: Response
): Promise<boolean> {
  if (!(await isAccountBlockedResponse(res))) return false;
  signalAccountBlocked();
  return true;
}

/** The draft Email support opens: to support, reference in the subject, account in the body. */
export function supportMailto(status: AccountBlockStatus | null): string {
  const known = status?.blocked ? status : null;
  const subject = known
    ? `Account review ${known.reference}`
    : "Account review";
  const opening = known
    ? `My account ${known.email} (reference ${known.reference}) was suspended. I think this is a mistake because:`
    : "My Vestra account was suspended. I think this is a mistake because:";
  const body = ["Hi Vestra team,", "", opening, "", ""].join("\n");
  return `mailto:${ACCOUNT_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function formatBlockedSince(iso: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const bus = new EventTarget();
const BLOCKED_EVENT = "vestra:account-blocked";
const UNBLOCKED_EVENT = "vestra:account-unblocked";

/** Ask the auth gate to re-check now (a socket or fetch was refused as account_blocked). */
export function signalAccountBlocked(): void {
  bus.dispatchEvent(new Event(BLOCKED_EVENT));
}

export function onAccountBlocked(listener: () => void): () => void {
  bus.addEventListener(BLOCKED_EVENT, listener);
  return () => bus.removeEventListener(BLOCKED_EVENT, listener);
}

/** The block is gone: re-dial the socket (socket.io never retries a middleware refusal). */
export function signalAccountUnblocked(): void {
  bus.dispatchEvent(new Event(UNBLOCKED_EVENT));
}

export function onAccountUnblocked(listener: () => void): () => void {
  bus.addEventListener(UNBLOCKED_EVENT, listener);
  return () => bus.removeEventListener(UNBLOCKED_EVENT, listener);
}
